import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { Navigation } from "../Landing/components/Navigation";
import { Footer } from "../Landing/components/Footer";
import type { Page } from "../types/Page";
import { useSecureVideoStream } from "../../hooks/useSecureVideoStream";
import { motion } from "framer-motion";
import { ShieldCheck, Server, BrainCircuit, HeartPulse, Globe, Landmark } from "lucide-react";
import "./SecurityPage.css";

const VIDEO_URL =
  "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260809_132544_b6ef0174-ed95-45ad-9a2f-ccb8acfbdce8.mp4";

const commitments = [
  {
    number: "01",
    title: "Protect Patient Privacy (PHI)",
    description:
      "We are dedicated to safeguarding Protected Health Information (PHI). Our security program exceeds HIPAA requirements to ensure patient data remains confidential and secure.",
  },
  {
    number: "02",
    title: "Secure Clinical Foundation",
    description:
      "We treat security as a patient safety issue, integrating strict controls and isolation into our annotation workflows and distributed infrastructure.",
  },
  {
    number: "03",
    title: "Enable Responsible Medical AI",
    description:
      "As partners in your clinical trials and research, we uphold the highest ethical standards and cryptographic verification for healthcare data handling.",
  },
];

interface FeatureCardProps {
  title: string;
  description: string;
  icon: React.ElementType;
  gradient: string;
  delay: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ title, description, icon: Icon, gradient, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, ease: "easeOut", delay }}
      className="relative flex flex-col justify-start items-start w-full max-w-[260px] md:max-w-[300px] group mx-auto"
    >
      {/* Glow Background */}
      <div 
        className="absolute w-full h-[260px] md:h-[300px] opacity-60 rounded-[40px] pointer-events-none"
        style={{ background: gradient, filter: "blur(45px)" }}
      />
      {/* Foreground Card with Gradient Border */}
      <div 
        className="relative self-stretch h-[260px] md:h-[300px] rounded-[40px] z-10 overflow-hidden"
        style={{
          border: '8px solid transparent',
          background: `linear-gradient(#1A1A1C, #1A1A1C) padding-box, ${gradient} border-box`
        }}
      >
        <div className="w-full h-full p-7 flex flex-col justify-between">
          <div className="text-white/90">
            <Icon size={32} strokeWidth={2.5} />
          </div>
          <div>
            <h3 className="text-white font-medium text-xl mb-3 tracking-tight">{title}</h3>
            <p className="text-gray-400 text-[14px] leading-[1.6] font-normal selection:bg-white/20">
              {description}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const certifications = [
  {
    name: "HIPAA Compliant",
    badge: "Health Data Standard",
    description:
      "Fully compliant with Health Insurance Portability and Accountability Act standards for handling and processing sensitive PHI across all annotation pipelines.",
    logo: HeartPulse,
  },
  {
    name: "SOC 2 Type II",
    badge: "Audited Security",
    description:
      "Independently verified security, availability, and confidentiality controls audited annually by leading cybersecurity compliance firms.",
    logo: ShieldCheck,
  },
  {
    name: "ISO 27001",
    badge: "Global Standard",
    description:
      "Certified information security management system (ISMS), ensuring a systematic and continuous approach to managing sensitive enterprise assets.",
    logo: Globe,
  },
  {
    name: "GDPR Compliant",
    badge: "Privacy by Design",
    description:
      "Strict adherence to European Union and international data protection regulations, with full data sovereignty and right-to-be-forgotten controls.",
    logo: Landmark,
  },
];

const securityPractices = [
  {
    category: "Data Protection",
    items: [
      "End-to-end encryption using TLS 1.3 in transit and AES-256 at rest",
      "Automatic PHI redaction and de-identification pipelines",
      "Strict data isolation between tenants, organizations, and projects",
      "Cryptographic data deletion and verifiable right-to-be-forgotten",
    ],
  },
  {
    category: "Access Control",
    items: [
      "Granular role-based access control (RBAC) with clinical workflow roles",
      "Enterprise Single Sign-On (SSO) with SAML 2.0 and OAuth 2.0 / OIDC",
      "Multi-factor authentication (MFA) mandatory across all personnel",
      "Comprehensive tamper-evident audit logging for all data operations",
    ],
  },
  {
    category: "Infrastructure Security",
    items: [
      "Network segmentation, micro-perimeters, and WAF firewall protection",
      "Dedicated VPC Peering and AWS Direct Connect for enterprise partners",
      "24/7 automated security monitoring and rapid incident response",
      "Regular penetration testing conducted by independent CREST-accredited firms",
    ],
  },
  {
    category: "Compliance & Auditing",
    items: [
      "Quarterly HIPAA and cybersecurity risk assessments",
      "Annual SOC 2 Type II and ISO 27001 third-party surveillance audits",
      "Custom Business Associate Agreements (BAA) and DPA support",
      "Continuous vulnerability scanning and automated patch management",
    ],
  },
];

export const SecurityPage: Page = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const secHeroCardRef = useRef<HTMLDivElement>(null);

  // Secure Video Stream with Chunked Delivery, Blob URL Isolation & Viewport Auto-Pause
  const { videoProps } = useSecureVideoStream(videoRef, {
    src: VIDEO_URL,
    containerRef: secHeroCardRef,
  });

  return (
    <div className="sec-page-root">
      {/* Black Navbar at top */}
      <Navigation />

      {/* Hero Section with Curved Card & Embedded Right Animation */}
      <section className="sec-hero-wrapper">
        <div
          ref={secHeroCardRef}
          className="sec-hero-card"
          onContextMenu={(e) => e.preventDefault()}
        >
          {/* Right Animation Video Plate (Monochrome / High-Tech Cyber Data Stream) */}
          <div className="sec-hero-video-box">
            <video
              ref={videoRef}
              className="sec-hero-video"
              {...videoProps}
            />
          </div>

          {/* Smooth blending scrim */}
          <div className="sec-hero-scrim" />

          {/* Left Text Content */}
          <div className="sec-hero-content">
            <h1 className="sec-hero-title">
              HIPAA-Grade Security
              <br />
              <span className="sec-hero-gradient-text">for Your Data</span>
            </h1>

            <p className="sec-hero-desc">
              Whether you're validating patient data, proprietary model outputs, or sensitive training datasets — trust,
              compliance, and privacy are embedded into our infrastructure at every level.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-1">
              <Link to="/contact" className="sec-hero-btn">
                <span>Secure system</span>
                <svg viewBox="0 0 21.5 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="butt" strokeLinejoin="miter">
                  <path d="M0 9H20.1" />
                  <path d="M12.1 1L20.1 9L12.1 17" />
                </svg>
              </Link>
            </div>

            {/* Quick trust metrics */}
            <div className="sec-hero-stats">
              <div>
                <strong>300+</strong> Clients
              </div>
              <div className="sec-hero-stats-dot" />
              <div>
                <strong>99%</strong> Satisfaction
              </div>
              <div className="sec-hero-stats-dot" />
              <div>
                <strong>SOC 2 & HIPAA</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content: Our Commitment */}
      <section className="sec-section">
        <div className="sec-container flex flex-col items-center text-center">
          <span className="sec-section-tag">// Ethical Foundation</span>
          <h2 className="sec-section-heading">Our Commitment</h2>
          <p className="sec-section-sub mb-12 max-w-2xl">
            Protecting sensitive healthcare records, model weights, and proprietary labeling operations is core to our
            mission.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-3 lg:gap-3 w-full max-w-[936px] font-sans">
            <FeatureCard 
              title={commitments[0].title}
              description={commitments[0].description}
              icon={ShieldCheck}
              delay={0.1}
              gradient="linear-gradient(137deg, #FF3D77 0%, #FFB1CE 45%, #FF9D3C 100%)"
            />
            <FeatureCard 
              title={commitments[1].title}
              description={commitments[1].description}
              icon={Server}
              delay={0.2}
              gradient="linear-gradient(137deg, #FFFFFF 0%, #7DD3FC 45%, #06B6D4 100%)"
            />
            <FeatureCard 
              title={commitments[2].title}
              description={commitments[2].description}
              icon={BrainCircuit}
              delay={0.3}
              gradient="linear-gradient(137deg, #4361EE 0%, #E0AEFF 45%, #F72585 100%)"
            />
          </div>
        </div>
      </section>

      {/* Main Content: Certifications & Compliance */}
      <section className="sec-section">
        <div className="sec-container">
          <span className="sec-section-tag">// Independently Verified</span>
          <h2 className="sec-section-heading">Certifications & Compliance</h2>
          <p className="sec-section-sub">
            Rigorous compliance certifications verified by accredited independent third-party auditors.
          </p>

          <div className="sec-grid-2">
            {certifications.map((cert, index) => {
              const Icon = cert.logo;
              return (
                <div key={index} className="sec-card relative overflow-hidden group">
                  {/* Faded watermark logo on the left */}
                  <div 
                    className="absolute -left-12 top-1/2 -translate-y-1/2 opacity-[0.06] group-hover:opacity-10 transition-opacity duration-700 pointer-events-none"
                    style={{
                      WebkitMaskImage: "linear-gradient(to right, black 20%, transparent 100%)",
                      maskImage: "linear-gradient(to right, black 20%, transparent 100%)"
                    }}
                  >
                    <Icon size={240} strokeWidth={1} />
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="sec-card-title" style={{ marginBottom: 0 }}>
                        {cert.name}
                      </h3>
                      <span className="text-xs font-mono px-2.5 py-1 rounded bg-white/5 border border-white/10 text-zinc-300">
                        {cert.badge}
                      </span>
                    </div>
                    <p className="sec-card-desc">{cert.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Compliance package notice card */}
          <div className="mt-8 p-6 rounded-2xl border border-white/10 bg-[#09090b] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <p className="font-mono text-sm text-zinc-400">
              Need access to our latest SOC 2 Type II audit report, penetration test findings, or HIPAA BAA execution?
            </p>
            <Link
              to="/contact"
              className="text-xs font-mono font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 px-5 py-2.5 rounded transition-colors whitespace-nowrap text-center"
            >
              Request Compliance Reports &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content: Security Practices */}
      <section className="sec-section">
        <div className="sec-container">
          <span className="sec-section-tag">// Defense In Depth</span>
          <h2 className="sec-section-heading">How We Protect Ourselves & Our Customers</h2>
          <p className="sec-section-sub">
            Comprehensive multi-layered controls across data pipelines, identity architectures, and physical facilities.
          </p>

          <div className="sec-grid-2">
            {securityPractices.map((practice, index) => (
              <div key={index} className="sec-practice-card">
                <div className="sec-practice-header">
                  <div className="sec-practice-indicator" />
                  <h3 className="sec-practice-title">{practice.category}</h3>
                </div>
                <ul className="sec-practice-list">
                  {practice.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="sec-practice-item">
                      <span className="sec-practice-bullet">&mdash;</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Contact CTA Banner */}
      <section className="sec-section" style={{ borderBottom: "none" }}>
        <div className="sec-container">
          <div className="sec-banner-card">
            <span className="sec-section-tag">// Deploy Securely</span>
            <h2 className="sec-section-heading" style={{ marginBottom: 0 }}>
              Ready to secure your clinical AI data workflows?
            </h2>
            <p className="sec-section-sub" style={{ marginBottom: 8 }}>
              Speak directly with our security architects to review your data handling requirements, custom VPC
              deployments, or BAA documentation.
            </p>
            <Link to="/contact" className="sec-hero-btn">
              <span>Contact Security Team</span>
              <svg viewBox="0 0 21.5 18" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M0 9H20.1" />
                <path d="M12.1 1L20.1 9L12.1 17" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

SecurityPage.title = "Security";
SecurityPage.path = "/security";
SecurityPage.exact = true;

export default SecurityPage;
